import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { FiPlus, FiMail, FiSend, FiUser, FiX } from 'react-icons/fi';

const InviteModal = ({ onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/tech-tasks/invite', { name, email, role: 'tech' });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send invite');
    } finally {
      setSaving(false);
    }
  };

  const field =
    'w-full px-3 py-2.5 rounded-lg border border-gray-300 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Add technical member</h2>
          <button type="button" onClick={onClose} className="p-2 text-gray-500">
            <FiX size={22} />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Only technical members can be added here. They sign in at the technical login page — not the main TaskMate app.
        </p>
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Name *</label>
            <input className={field} required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Email *</label>
            <input className={field} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-semibold disabled:opacity-50">
              {saving ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TechTeam = () => {
  const { isTechAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [resending, setResending] = useState(null);
  const [message, setMessage] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tech-tasks/members');
      setUsers(res.data.users || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isTechAdmin) fetchUsers();
  }, [isTechAdmin]);

  const resend = async (id) => {
    setResending(id);
    setMessage('');
    try {
      await api.post(`/tech-tasks/members/${id}/resend-invite`);
      setMessage('Invite email sent again.');
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not resend invite');
    } finally {
      setResending(null);
    }
  };

  if (!isTechAdmin) return <Navigate to="/tech" replace />;

  return (
    <div className="space-y-4 max-w-2xl pb-8">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-slate-400 text-sm mt-1">Technical members only — not business TaskMate users</p>
        </div>
        <button
          type="button"
          onClick={() => setShowInvite(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-500 text-slate-950 font-semibold text-sm touch-manipulation"
        >
          <FiPlus size={18} />
          Add member
        </button>
      </div>

      {message && <p className="text-sm text-teal-300">{message}</p>}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-400" />
        </div>
      ) : users.length === 0 ? (
        <p className="text-center text-slate-500 py-12">No technical members yet. Invite someone to get started.</p>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-teal-500/20 text-teal-300 flex items-center justify-center shrink-0">
                    <FiUser size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-white truncate">{u.name}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1 truncate">
                      <FiMail size={12} />
                      {u.email}
                    </p>
                    <p className="text-[11px] text-teal-400 mt-0.5">
                      {u.role === 'tech_admin' ? 'Tech admin' : 'Member'}
                    </p>
                  </div>
                </div>
                <span
                  className={`text-[10px] px-2 py-1 rounded-full shrink-0 ${
                    u.is_active === false
                      ? 'bg-red-500/20 text-red-300'
                      : 'bg-emerald-500/20 text-emerald-300'
                  }`}
                >
                  {u.is_active === false ? 'Inactive' : 'Active'}
                </span>
              </div>
              {u.invite_token_expires && (
                <button
                  type="button"
                  onClick={() => resend(u.id)}
                  disabled={resending === u.id}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber-500/15 text-amber-200 text-sm font-medium disabled:opacity-50"
                >
                  <FiSend size={14} />
                  {resending === u.id ? 'Sending…' : 'Resend invite email'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onSuccess={() => {
            setMessage('Invite sent. They will set a password from the email.');
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

export default TechTeam;
